/**
 * First Component displayed on the Landing page.
 */

/**
 * TODO: Replace the "Personality Survey" Heading with unlocked survey title post login.
 */

/* @ts-ignonre */

import {
  Box,
  Flex,
  Heading,
  Image,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SurveyRoutes, maxCompensation, pathsForFilters } from "config/Consts";
import { useHistory, useLocation } from "react-router-dom";
import Harvard from "../../assets/harvard-business-school-logo-vector.svg";
import UM from "../../assets/um.png";
import GLBLogo from "../../assets/gbl_logo.svg";
import { ModalTypes } from "../../config/types/modals";
import useAuth from "../../hooks/useAuth";
import useModal from "../../hooks/useModal";
import {
  OutlineButton,
  PrimaryButton,
  SecondaryButton,
} from "../base/atoms/HJSButton";
import { useGetSurveysInfoQuery } from "queries";
import { calculateUnlockedTime } from "utils/getSurvey";
import useOpenVerificationModal from "hooks/useOpenVerificationModal";
import { useEffect, useState } from "react";
import { useAutoLoginQuery } from "_actions/AuthActions";
import Eligibility from "pages/Eligibility";

const getNextUnlockedSurvey = (surveys: any[]): string => {
  let unlockedSurvey = -1;
  for (let i = 0; i < surveys?.length; i++) {
    const currentSurvey = surveys[i];

    // Check if the survey is not unlocked and not completed
    if (
      !(currentSurvey.isUnlocked as boolean) &&
      !(currentSurvey.isCompleted as boolean)
    ) {
      if (i + 1 < surveys.length && (surveys[i + 1].isCompleted as boolean)) {
        // If the next survey is completed, skip to the survey after it
        i++;
      } else {
        // If the next survey is not completed, or there is no next survey
        unlockedSurvey = i;
        break;
      }
    }
  }
  if (unlockedSurvey > -1 && unlockedSurvey < surveys.length) {
    return calculateUnlockedTime(surveys[unlockedSurvey]).toString();
  }

  return "";
};

const Welcome: () => JSX.Element = () => {
  const { isAuthenticated, userDetails, setAuthToken } = useAuth();
  const { onOpen, changeModalType } = useModal();
  const history = useHistory();

  const { data: surveysInfoRaw } = useGetSurveysInfoQuery();
  const { mutate: autoLogin } = useAutoLoginQuery();

  // Normalize: API returns array directly or wrapped in axios response
  const surveysInfo: any[] | undefined = Array.isArray(surveysInfoRaw)
    ? surveysInfoRaw
    : Array.isArray(surveysInfoRaw?.data)
    ? surveysInfoRaw.data
    : undefined;

  const openVerificationModal = useOpenVerificationModal();
  const [unlockedIncompleteSurveys, setUnlockedIncompleteSurveys] =
    useState<any>([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get("user");
  interface SurveyKeyMap {
    key: string;
  }

  interface FollowUpSurvey extends SurveyKeyMap {
    survey: {
      key: "followUp1" | "followUp2" | "followUp3";
    };
  }

  const btnHandler = (): void => {
    if (
      !(userDetails?.isEligibilitySurveyDone as boolean) &&
      (userDetails?.is_email_verified as boolean)
    ) {
      history.push("/survey/eligiblity");
    } else if (!isAuthenticated) {
      changeModalType(ModalTypes.LOGIN);
      onOpen();
    } else if (
      (userDetails?.isEligible as boolean) &&
      unlockedIncompleteSurveys?.[0]?.survey?.name === "Personality Survey"
    ) {
      history.push(SurveyRoutes.personality);
    } else {
      const followUpSurveys: FollowUpSurvey[] =
        unlockedIncompleteSurveys.filter((surveyData: any) =>
          ["followUp1", "followUp2", "followUp3"].includes(
            surveyData.survey.key
          )
        );

      if (followUpSurveys.length > 0) {
        const followUpKey: "followUp1" | "followUp2" | "followUp3" =
          followUpSurveys[0].survey.key;

        history.push(SurveyRoutes[followUpKey]);
      } else {
        openVerificationModal();
      }
    }
  };

  useEffect(() => {
    if (userId !== null && userId !== undefined) {
      localStorage.clear();
      autoLogin(
        {
          user: userId,
        },
        {
          onSuccess: (resp: any) => {
            setAuthToken(resp.token);

            if (surveysInfo?.[0]?.isCompleted === false) {
              history.push(SurveyRoutes.personality);
            } else if (
              surveysInfo?.[1]?.isCompleted === false &&
              surveysInfo?.[1]?.isUnlocked === false &&
              userDetails?.googleAddress === true &&
              userDetails?.address === true
            ) {
              history.push("/jobs/search");
            } else if (
              surveysInfo?.[1]?.isCompleted === false &&
              surveysInfo?.[1]?.isUnlocked === true
            ) {
              history.push(SurveyRoutes.followUp1);
            } else if (
              surveysInfo?.[1]?.isCompleted === true &&
              surveysInfo?.[1]?.isUnlocked === true &&
              surveysInfo?.[2]?.isCompleted === false &&
              surveysInfo?.[2]?.isUnlocked === false &&
              userDetails?.googleAddress === true &&
              userDetails?.address === true
            ) {
              history.push("/jobs/search");
            } else if (
              surveysInfo?.[2]?.isCompleted === false &&
              surveysInfo?.[2]?.isUnlocked === true
            ) {
              history.push(SurveyRoutes.followUp2);
            } else if (
              surveysInfo?.[2]?.isCompleted === true &&
              surveysInfo?.[2]?.isUnlocked === true &&
              surveysInfo?.[3]?.isCompleted === false &&
              surveysInfo?.[3]?.isUnlocked === false &&
              userDetails?.googleAddress === true &&
              userDetails?.address === true
            ) {
              history.push("/jobs/search");
            } else if (
              surveysInfo?.[3]?.isCompleted === false &&
              surveysInfo?.[3]?.isUnlocked === true
            ) {
              history.push(SurveyRoutes.followUp3);
            } else if (
              surveysInfo?.[3]?.isCompleted === true &&
              surveysInfo?.[3]?.isUnlocked === true &&
              userDetails?.googleAddress === true &&
              userDetails?.address === true
            ) {
              history.push("/jobs/search");
            } else {
              history.push("/");
            }
          },
        }
      );
    }
  }, [userId, surveysInfo]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (surveysInfo) {
      const filteredSurveys = surveysInfo.filter(
        (survey: any) =>
          // eslint-disable-next-line no-extra-boolean-cast
          Boolean(survey.isUnlocked) && !Boolean(survey.isCompleted)
      );
      setUnlockedIncompleteSurveys(filteredSurveys);
    }
  }, [surveysInfo]);

  return (
    <>
      <Stack
        w="100%"
        direction={{ base: "column-reverse", lg: "row" }}
        pl={{
          base: 4,
          sm: 8,
          md: 20,
          lg: 32,
          // "2xl": 40,
          "3xl": 80,
        }}
        pr={{
          base: 4,
          sm: 8,
          md: 20,
          lg: "initial",
        }}
        gap={{
          base: 4,
          sm: 8,
          lg: 4,
          xl: 20,
        }}
        mb={10}
        mt={4}
      >
        <Flex
          flexDir="column"
          alignItems="flex-start"
          textAlign="left"
          h="full"
          // className="w-full md:w-[50%]"
          width={{ base: "100%", lg: "50%" }}
          margin="auto"
          // ml={{ xl: "4rem" }}
          // px={32}
        >
          <Heading
            as="h1"
            mb={6}
            color="teal.900"
            fontSize={[36, 40, 48]}
            fontWeight="700"
          >
            Earn up to ${maxCompensation}
          </Heading>
          <Text fontWeight="600" fontSize="2rem" color="hjsBlack">
            Welcome folks!
          </Text>
          <Text
            fontWeight="500"
            mt={6}
            mr={6}
            fontSize="1.25rem"
            color="hjsBlack"
            width="100%"
          >
            This is a research study by Harvard Business School, University of
            Michigan, and Good Business Lab. You can participate in this study
            by completing the surveys displayed below.
          </Text>
          <Flex alignItems="center">
            <Image src={Harvard} h="120px" alt="Harvard Business School" />
            <Image src={UM} h="50px" alt="University of Michigan" />
            <Image src={GLBLogo} h="50px" alt="good business labs" />
          </Flex>
        </Flex>
        <Box
          h="full" /* className="w-[90%] !m-auto md:!m-0 md:w-[50%]" */
          width={{
            base: "100%",
            lg: "50%",
          }}
        >
          <VStack
            bgColor="teal.50"
            shadow="md"
            h="full"
            w="full"
            // pr={{ base: 2, xl: 28, "2xl": 32 }}
            // pl={{ base: 2, xl: 4 }}
            pt={{ base: 10, md: 20 }}
            pb={{ base: 10, md: 24 }}
            px={4}
            // justifyContent="space-between"
            // alignItems={[]}
            // gap={90}
          >
            {userDetails?.isEligible === false && (
              <>
                <Heading
                  as="h1"
                  color="teal.900"
                  fontWeight="700"
                  fontSize={[20, 25, 32]}
                >
                  Eligibility Survey
                </Heading>
                <Text
                  fontWeight="700"
                  fontSize={20}
                  color="teal.900"
                  textAlign="center"
                  // marginBottom={110}
                  className="!mt-16 !mb-8"
                >
                  Are you ready to start the survey?
                </Text>
                <Flex
                  alignItems="center"
                  w="full"
                  justifyContent="center"
                  gap={4}
                >
                  <PrimaryButton
                    px={67}
                    py={2.5}
                    // mb={6}
                    fontWeight="600"
                    fontSize={{ base: 14, md: 16 }}
                    onClick={() => history.push("/survey/eligiblity")}
                  >
                    Yes/Maybe
                  </PrimaryButton>
                  <OutlineButton
                    fontWeight="600"
                    px={43.5}
                    py={2.5}
                    fontSize={{ base: 14, md: 16 }}
                    onClick={() => history.push("/about")}
                  >
                    I need more info
                  </OutlineButton>
                </Flex>
              </>
            )}
            {unlockedIncompleteSurveys?.length > 0 ||
              (!isAuthenticated && (
                <>
                  <Heading
                    as="h1"
                    color="teal.900"
                    fontWeight="700"
                    fontSize={[20, 25, 32]}
                  >
                    {!isAuthenticated
                      ? "Personality Survey"
                      : unlockedIncompleteSurveys?.[0]?.survey?.name}
                  </Heading>
                  <Text
                    fontWeight="700"
                    fontSize={20}
                    color="teal.900"
                    textAlign="center"
                    // marginBottom={110}
                    className="!mt-16 !mb-8"
                  >
                    Are you ready to start the survey?
                  </Text>
                  <Flex
                    alignItems="center"
                    w="full"
                    justifyContent="center"
                    gap={4}
                  >
                    <PrimaryButton
                      px={67}
                      py={2.5}
                      // mb={6}
                      fontWeight="600"
                      fontSize={{ base: 14, md: 16 }}
                      onClick={btnHandler}
                    >
                      Yes/Maybe
                    </PrimaryButton>
                    <OutlineButton
                      fontWeight="600"
                      px={43.5}
                      py={2.5}
                      fontSize={{ base: 14, md: 16 }}
                      onClick={() => history.push("/about")}
                    >
                      I need more info
                    </OutlineButton>
                  </Flex>
                </>
              ))}

            {userDetails?.isEligible === true &&
            (unlockedIncompleteSurveys?.length > 0 || !isAuthenticated) ? (
              <>
                <Heading
                  as="h1"
                  color="teal.900"
                  fontWeight="700"
                  fontSize={[20, 25, 32]}
                >
                  {!isAuthenticated
                    ? "Personality Survey"
                    : unlockedIncompleteSurveys?.[0]?.survey?.name}
                </Heading>
                <Text
                  fontWeight="700"
                  fontSize={20}
                  color="teal.900"
                  textAlign="center"
                  // marginBottom={110}
                  className="!mt-16 !mb-8"
                >
                  Are you ready to start the survey?
                </Text>
                <Flex
                  alignItems="center"
                  w="full"
                  justifyContent="center"
                  gap={4}
                >
                  <PrimaryButton
                    px={67}
                    py={2.5}
                    // mb={6}
                    fontWeight="600"
                    fontSize={{ base: 14, md: 16 }}
                    onClick={btnHandler}
                  >
                    Yes/Maybe
                  </PrimaryButton>
                  <OutlineButton
                    fontWeight="600"
                    px={43.5}
                    py={2.5}
                    fontSize={{ base: 14, md: 16 }}
                    onClick={() => history.push("/about")}
                  >
                    I need more info
                  </OutlineButton>
                </Flex>
              </>
            ) : userDetails?.layer === 1 && userDetails?.isEligible === true ? (
              <>
                {surveysInfo?.[3]?.isCompleted === true ? (
                  <Box>
                    <Text
                      fontWeight="700"
                      fontSize={[15, 20, 24]}
                      color="teal.900"

                      // marginBottom={110}
                      // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                    >
                      Thank you for participating!
                    </Text>
                  </Box>
                ) : (
                  <Box>
                    <Text
                      fontWeight="700"
                      fontSize={[15, 20, 24]}
                      color="teal.900"

                      // marginBottom={110}
                      // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                    >
                      The next survey will
                    </Text>
                    <Text
                      fontWeight="700"
                      fontSize={[15, 20, 24]}
                      color="teal.900"
                      ml={0.5}

                      // marginBottom={110}
                      // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                    >
                      unlock in {getNextUnlockedSurvey(surveysInfo ?? [])}
                    </Text>
                  </Box>
                )}

                <SecondaryButton
                  fontWeight="600"
                  px={43.5}
                  py={2.5}
                  className="!mt-16"
                  onClick={() => history.push("/jobs/search")}
                >
                  Search Jobs
                </SecondaryButton>
              </>
            ) : (
              <>
                {surveysInfo?.[2]?.isCompleted === true &&
                userDetails?.isEligible === true ? (
                  <>
                    <Box>
                      <Text
                        fontWeight="700"
                        fontSize={[15, 20, 24]}
                        color="teal.900"

                        // marginBottom={110}
                        // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                      >
                        Thank you for participating!
                      </Text>
                    </Box>
                    <SecondaryButton
                      fontWeight="600"
                      px={43.5}
                      py={2.5}
                      className="!mt-16"
                      onClick={() => history.push("/jobs/search")}
                    >
                      Search Jobs
                    </SecondaryButton>
                  </>
                ) : userDetails?.isEligible === true ? (
                  <>
                    {" "}
                    <Box>
                      <Text
                        fontWeight="700"
                        fontSize={[15, 20, 24]}
                        color="teal.900"

                        // marginBottom={110}
                        // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                      >
                        The next survey will
                      </Text>
                      <Text
                        fontWeight="700"
                        fontSize={[15, 20, 24]}
                        color="teal.900"
                        ml={0.5}

                        // marginBottom={110}
                        // className=" !mt-10 !mb-8 md:!mt-28 md:!mb-24 "
                      >
                        unlock in {getNextUnlockedSurvey(surveysInfo ?? [])}
                      </Text>
                    </Box>
                    <SecondaryButton
                      fontWeight="600"
                      px={43.5}
                      py={2.5}
                      className="!mt-16"
                      onClick={() => history.push("/jobs/search")}
                    >
                      Search Jobs
                    </SecondaryButton>
                  </>
                ) : null}
              </>
            )}
          </VStack>
        </Box>
      </Stack>
    </>
  );
};

export default Welcome;
